import type { OverrideQuizRequest, SubmitQuizRequest } from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as quizApi from "@/lib/api/flow-instances/quiz";
import { useInvalidateFlowCompletion } from "./use-flow-completion";

export const quizKeys = {
  flowInstanceValue: (id: string | null) =>
    ["flowInstances", id, "quiz", "flow-instance-value"] as const,
  quizzes: (id: string | null) => ["flowInstances", id, "quiz", "quizzes"] as const,
};

/**
 * The flow's quizzes and this client's standing on each. This is what the
 * widget renders from — the questions are not here, and cannot be: they are
 * drawn per attempt by `useStartQuiz`.
 */
export function useInstanceQuizzes(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: quizKeys.quizzes(id),
    queryFn: () => quizApi.getQuizzes(id!),
    enabled: !!id,
  });
  return { quizzes: data ?? null, error, isLoading };
}

export function useQuizFlowInstanceValue(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: quizKeys.flowInstanceValue(id),
    queryFn: () => quizApi.getFlowInstanceValue(id!),
    enabled: !!id,
  });
  return {
    inputData: data?.inputData ?? null,
    calculatedData: data?.calculatedData ?? null,
    error,
    isLoading,
  };
}

/**
 * Deliberately no cache invalidation: starting an attempt draws questions but
 * changes nothing the widget shows, and the server treats a repeat start as a
 * resume. Refetching here would only race the modal that is opening.
 */
/** QL-505 — `subjectId` addresses one party when several answer (a group member, or a representative when 2+ are registered). */
export function useStartQuiz(instanceId: string) {
  return useMutation({
    mutationFn: ({ quizId, subjectId }: { quizId: string; subjectId?: string }) =>
      quizApi.startQuiz(instanceId, quizId, subjectId),
  });
}

/** Everything that changes after a graded attempt or an override. */
function useQuizWriteHandlers(instanceId: string) {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  return () => {
    // Neither response carries the value blob, so refetch rather than patch.
    queryClient.invalidateQueries({ queryKey: quizKeys.quizzes(instanceId) });
    queryClient.invalidateQueries({ queryKey: quizKeys.flowInstanceValue(instanceId) });
    // The quiz gates Submit — a pass is the only thing that opens it.
    invalidateCompletion(instanceId);
  };
}

export function useSubmitQuiz(instanceId: string) {
  const onWritten = useQuizWriteHandlers(instanceId);
  return useMutation({
    mutationFn: (request: SubmitQuizRequest) => quizApi.submitQuiz(instanceId, request),
    onSuccess: onWritten,
  });
}

/** Advisor-only. The public flow has no equivalent hook. */
export function useOverrideQuiz(instanceId: string) {
  const onWritten = useQuizWriteHandlers(instanceId);
  return useMutation({
    mutationFn: (request: OverrideQuizRequest) => quizApi.overrideQuiz(instanceId, request),
    onSuccess: onWritten,
  });
}
